// Common Jet
import type { ActionModel } from '@jet/environment/types/models';
import type { Opt } from '@jet/environment/types/optional';

// Classical API Types
import type {
    ClassicalAPIResponseAction,
    ClassicalGridItem,
    ClassicalShelfItem,
    PayloadParams,
} from '@jet-app/music-classical/api/types';

export const enum PlayActionKind {
    Name = 'playAction',
}

export interface PlayActionItem {
    readonly type: string;
    readonly identifiers: {
        id: string;
        versionHash?: string;
    };
    attributes?: Opt<Record<string, unknown>>;
}

export interface PlayAction extends ActionModel {
    readonly $kind: PlayActionKind.Name;

    items: Opt<PlayActionItem[]>;
    groupingIdentifier?: Opt<string>;
    containerItem?: Opt<PlayActionItem>;
}

export interface GetPlaybackActionFromQuery extends ActionModel {
    $kind: 'getPlaybackActionFromQuery';
    query: string;
}

const CLASSICAL_TYPE_TO_KIND_MAPPING = {
    songs: 'song',
};

export const makeGetPlaybackActionFromQuery = (
    query?: string,
): GetPlaybackActionFromQuery => {
    if (!query) {
        return null;
    }

    return {
        $kind: 'getPlaybackActionFromQuery',
        query,
        actionMetrics: {
            data: [],
            custom: {},
        },
    };
};

export function playActionForItem(
    item: ClassicalGridItem | ClassicalShelfItem,
) {
    // Recording lockups will have a query for the playActionUrl that needs
    // to be called in order to get the play action data.
    if (item.type === 'recording' || !item.playButtonAction) {
        return makeGetPlaybackActionFromQuery(item.playActionUrl);
    }

    return playActionForClassicalPlayAction(item.playButtonAction);
}

export function playActionForClassicalPlayAction(
    action: ClassicalAPIResponseAction,
    payload?: PayloadParams,
) {
    const attributes = {};

    if (!action) {
        return null;
    }

    if (action.startIndex) {
        attributes.startWith = action.startIndex;
    }

    const containerItem = {
        type: action?.containerPlayParam?.kind,
        identifiers: {
            id: action.containerPlayParam?.id,
        },
        attributes,
    };

    if (action.containerPlayParam?.versionHash) {
        containerItem.identifiers.versionHash =
            action.containerPlayParam.versionHash;
    }

    return {
        $kind: PlayActionKind.Name,
        actionMetrics: { data: [], custom: {} },
        items: !!payload
            ? [
                  {
                      type: CLASSICAL_TYPE_TO_KIND_MAPPING[payload.type],
                      identifiers: {
                          id: payload.id,
                      },
                      attributes,
                  },
              ]
            : // rdar://150296128 (Album pages 500)
              // Ensure we are not trying to process playParam values of `null`
              action?.playParams
                  ?.filter((param) => !!param)
                  ?.map((playParam) => ({
                      type: playParam.kind,
                      identifiers: {
                          id: playParam.id,
                      },
                      attributes,
                  })),
        containerItem,
    } as PlayAction;
}
